import React, { useState, useEffect, useCallback } from 'react';
import { FileSignature, X, Loader, Eye, Lock } from 'lucide-react';
import { buildSubcontractorAgreementPdfBytes, bytesToBase64 } from '../utils/subcontractorAgreement';
import { SignaturePad } from './LeaseSignCard';

// ── Subcontractor agreement e-signature ───────────────────────────────────────
// Sibling of LeaseSignCard. Shows in the portal when the signed-in driver has a
// pending HPR subcontractor agreement. The modal rebuilds the exact PDF from the
// stored fields (black & gold, same engine as the lease), stamps the driver's
// signature on the Execution page and submits the signed bytes. Exported modal so an
// admin could complete a signing from the Contracts tab too.

const AGREEMENT_URL = '/.netlify/functions/subcontractor-agreement-sb';
const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function agreementToData(c, extra = {}) {
  return {
    subcontractor: {
      entity: c.subEntity || c.driverName || '',
      abn: c.subAbn || '',
      acn: c.subAcn || '',
      address: c.subAddress || '',
      repName: c.subRepName || c.driverName || '',
      repPosition: c.subRepPosition || 'Director',
    },
    hprAddress: c.hprAddress || '',
    agreementDate: c.agreementDate || '',
    commencementDate: c.commencementDate || '',
    expiryDate: c.expiryDate || '',
    deliveryArea: c.deliveryArea || '',
    baseRate: c.baseRate || 0,
    extraRates: Array.isArray(c.extraRates) ? c.extraRates : [],
    dcaNumber: c.dcaNumber || '',
    state: c.state || 'New South Wales',
    hprSignature: c.hprSignature || null,
    hprSignatory: c.hprSignatory || '',
    hprPosition: c.hprPosition || '',
    hprSignedDate: c.hprSignedDate || '',
    ...extra,
  };
}

// Review-and-sign modal. onSigned(agreementId) fires after a successful submit.
export function ContractorSignModal({ agreement, onClose, onSigned }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [signature, setSignature] = useState('');
  const [printName, setPrintName] = useState(agreement.subRepName || agreement.driverName || '');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const entity = agreement.subEntity || agreement.driverName || agreement.driverLogin;

  const build = (lesseeSig) => buildSubcontractorAgreementPdfBytes(
    agreementToData(agreement, lesseeSig
      ? { subSignature: lesseeSig, subSignedDate: todayLocalISO(), subPrintName: printName }
      : {}),
  );

  // Preview (HPR pre-signature shows; driver unsigned).
  useEffect(() => {
    let url = ''; let alive = true;
    (async () => {
      try {
        const bytes = await build(null);
        if (!alive) return;
        url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        setPreviewUrl(url);
      } catch { setPreviewUrl(''); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [agreement]);

  const submit = async () => {
    if (!signature) return;
    setBusy(true);
    try {
      const bytes = await build(signature);
      const res = await fetch(AGREEMENT_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'signAgreement', id: agreement.id, signedPdfBase64: bytesToBase64(bytes), signedName: printName }),
      }).then((r) => r.json());
      if (res.success) { if (onSigned) onSigned(agreement.id); onClose(); }
      else alert(`Could not submit: ${res.error || 'unknown error'}`);
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ width: 680, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Subcontractor Agreement</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ padding: 20 }}>
          <p style={{ margin: '0 0 12px', fontSize: '0.84rem', color: 'var(--hp-dim)' }}>
            Please read the agreement for <strong style={{ color: 'var(--hp-text)' }}>{entity}</strong>, then sign below. This is a legal document — you are encouraged to seek independent advice before signing.
          </p>

          {previewUrl ? (
            <iframe title="Agreement preview" src={previewUrl} style={{ width: '100%', height: 360, border: '1px solid var(--hp-border)', borderRadius: 8, background: '#fff' }} />
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--hp-muted)' }}><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Building document…</div>
          )}
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--hp-gold)', marginTop: 6 }}>
              <Eye size={12} /> Open full document
            </a>
          )}

          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>Signatory name (director / authorised signatory)</div>
            <input
              value={printName}
              onChange={(e) => setPrintName(e.target.value)}
              placeholder="Your full name"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '0.95rem', boxSizing: 'border-box', marginBottom: 10 }}
            />
            <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>Your signature</div>
            <SignaturePad onChange={setSignature} defaultName={printName} />
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: '0.82rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
            <span>I, {printName || 'the signatory'}, am authorised to sign for {entity} and agree to be bound by this Subcontractor Agreement.</span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={!signature || !agree || !printName.trim() || busy} onClick={submit}>
            {busy ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</> : <><Lock size={14} /> Sign &amp; submit</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function ContractorAgreementCard({ shift, onCountChange }) {
  const driverLogin = (shift?.driver || '').toUpperCase();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(null);

  const setPendingSynced = useCallback((next) => {
    setPending((prev) => {
      const val = typeof next === 'function' ? next(prev) : next;
      if (onCountChange) onCountChange(val.length);
      return val;
    });
  }, [onCountChange]);

  const load = useCallback(async () => {
    try {
      const d = await fetch(AGREEMENT_URL, { method: 'POST', body: JSON.stringify({ action: 'getMyAgreements' }) }).then((r) => r.json());
      if (d.success) setPendingSynced(d.agreements || []);
    } catch { /* silent — portal shouldn't break on this */ }
  }, [setPendingSynced]);

  useEffect(() => { if (driverLogin) load(); }, [driverLogin, load]);

  const decline = async (c) => {
    const reason = window.prompt(`Decline the subcontractor agreement for ${c.subEntity || c.driverName}? Optionally add a reason:`, '');
    if (reason === null) return;
    try {
      const res = await fetch(AGREEMENT_URL, { method: 'POST', body: JSON.stringify({ action: 'declineAgreement', id: c.id, reason }) }).then((r) => r.json());
      if (res.success) setPendingSynced((prev) => prev.filter((x) => x.id !== c.id));
      else alert(`Could not decline: ${res.error || 'unknown error'}`);
    } catch (e) { alert(`Error: ${e.message}`); }
  };

  if (!pending.length) return null;

  return (
    <div style={{ width: '100%', marginBottom: 16 }}>
      {pending.map((c) => (
        // A narrow phone kept the buttons at full width and crushed the text to one
        // word per line — worse, they ended up sitting on top of it. Wrap instead.
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '14px 16px', borderRadius: 12, background: 'rgba(212,175,55,0.08)', border: '1px solid var(--hp-gold)', marginBottom: 10 }}>
          <FileSignature size={22} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
          <div style={{ flex: '1 1 190px', minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Subcontractor agreement to sign</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
              {c.subEntity || c.driverName}{c.baseRate ? ` · $${Number(c.baseRate).toFixed(2)} per parcel` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(c)}>Review &amp; sign</button>
            <button className="btn btn-ghost btn-sm" onClick={() => decline(c)}>Decline</button>
          </div>
        </div>
      ))}

      {open && (
        <ContractorSignModal
          agreement={open}
          onClose={() => setOpen(null)}
          onSigned={(id) => setPendingSynced((prev) => prev.filter((c) => c.id !== id))}
        />
      )}
    </div>
  );
}
